import React from "react";
import { IBillingGroupList, IOrg, IOrgPersonListPaginated } from "@schema";
import { createRefetchContainer, QueryRenderer, RelayRefetchProp } from "react-relay";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Select,
  MenuItem,
  IconButton,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { GroupAdd, Close, LockOutlined } from "@material-ui/icons";

import { /*Person,*/ PersonSelector } from "@clockwise/web-commons/src/ui/person-selector";
import spacing from "@clockwise/web-commons/src/styles/spacing.styles";
import { headerColor, greys } from "@clockwise/web-commons/src/styles/color.styles";
import { getValue } from "@util/errorable.util";

import { fragments, query } from "./BulkInvite.gql";
import { styles } from "./BulkInvite.style";
import { canInviteToGroup, normalizePersons, sendInvites, sortPersons } from "./BulkInvite.util";
import { wrapWithClassComponent } from "@clockwise/web-commons/src/util/react.util";
import { getCurrentEnvironment } from "@src/state/relay-environment";
import { compareBooleans } from "@src/util/sort.util";

// Copied from web-commons/ui/person-selector because types cannot be imported between packages
interface Person {
  email: string;
  name: { givenName: string; familyName: string };
  externalImageUrl?: string;
  isSuggested: boolean;
}

interface PersonWithId extends Person {
  id: string;
}

// Invite UI

const useStyles = makeStyles(styles);

export interface IProps {
  billingGroupList: IBillingGroupList | null;
  open: boolean;
  onClose: () => void;
  onCloseAfterInvite: () => void;
  org: IOrg;
  relay: RelayRefetchProp;
}

const FREE_PLAN = "free";
export const InvitePanelCmpt = ({
  billingGroupList,
  open,
  onClose,
  onCloseAfterInvite,
  org,
  relay,
}: IProps) => {
  const classes = useStyles();

  const billingGroupOptions = React.useMemo(
    () =>
      (billingGroupList?.list ?? [])
        .map((group) => {
          return { id: group.id, name: group.name, needsAdminApproval: !canInviteToGroup(group) };
        })
        .concat({
          id: FREE_PLAN,
          name: "Free plan",
          needsAdminApproval: false,
        })
        .sort((a, b) => compareBooleans(a.needsAdminApproval, b.needsAdminApproval)),
    [billingGroupList?.list],
  );

  const [billingGroupId, setBillingGroupId] = React.useState(
    () =>
      billingGroupOptions.find(({ needsAdminApproval }) => !needsAdminApproval)?.id ?? FREE_PLAN,
  );

  const selectedBillingGroup = billingGroupOptions.find(({ id }) => id === billingGroupId);

  const [selected, setSelected] = React.useState<PersonWithId[]>([]);

  const suggested = useSuggestions({
    persons: getValue(org.orgPersonListPaginatedErrorable),
    selections: selected.map(({ email }) => email),
  });

  const handleSearch = React.useCallback(
    (newQuery: string) => {
      relay.refetch({
        orgRelayId: org.id,
        queryInput: newQuery,
      });
    },
    [relay],
  );

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent dividers>
        <IconButton className={classes.closeButton} onClick={onClose}>
          <Close />
        </IconButton>
        <Typography
          variant="h2"
          className={classes.heading}
          style={{ fontSize: 22, fontWeight: "bold", color: headerColor, marginBottom: 20 }}
        >
          Invite members
        </Typography>
        <Typography className={classes.subheading} variant="body2" style={{ color: greys.slate }}>
          {selectedBillingGroup?.needsAdminApproval
            ? "New users will be invited as free users immediately. Admins will receive a request to accept new additions to the subscription."
            : "After you invite new members, they will receive an email invitation with signup instructions. "}
        </Typography>

        <div className={classes.primaryControls}>
          <PersonSelector
            selections={selected}
            suggestions={suggested}
            onAdd={(person) =>
              setSelected((prev) => [...prev.filter((p) => p.email !== person.email), person])
            }
            onDelete={(person) =>
              setSelected((prev) => prev.filter((p) => p.email !== person.email))
            }
            onSearch={handleSearch}
            placeholder="Search by name or email"
            fullWidth
          />
        </div>
        <div>
          <Select
            disableUnderline
            value={billingGroupId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setBillingGroupId(e.target.value)
            }
            renderValue={(value: string) => {
              const name = billingGroupOptions.find(({ id }) => id === value)?.name;
              return name ? (
                <span className={classes.billingGroupSelected}>
                  <GroupAdd style={{ color: greys.slate, marginRight: spacing.sm }} />
                  Add to {name}
                </span>
              ) : null;
            }}
          >
            {billingGroupOptions.map(({ id, name, needsAdminApproval }) => (
              <MenuItem classes={{ root: classes.billingGroupOption }} key={id} value={id}>
                <div>
                  Add to {name}
                  {needsAdminApproval && (
                    <div className={classes.menuItemWarning}>
                      <LockOutlined fontSize="small" style={{ marginRight: spacing.xs }} />
                      <span>Admin approval needed</span>
                    </div>
                  )}
                </div>
              </MenuItem>
            ))}
          </Select>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={selected.length === 0}
          color="primary"
          variant="text"
          size="large"
          onClick={() => {
            sendInvites(selected, {
              relayEnv: relay.environment,
              orgId: org.id,
              billingGroupId: billingGroupId !== FREE_PLAN ? billingGroupId : null,
            });
            onCloseAfterInvite();
          }}
        >
          {selectedBillingGroup?.needsAdminApproval ? "Send invite request to admin" : "Invite"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface IUseSuggestions {
  persons: IOrgPersonListPaginated | null;
  selections: string[];
  limit?: number;
}
const useSuggestions = ({ persons, selections, limit = 10 }: IUseSuggestions): PersonWithId[] => {
  const normalizedPersons = React.useMemo(() => normalizePersons(persons?.persons ?? []), [
    persons?.persons,
  ]);

  const suggestions = React.useMemo(
    () =>
      sortPersons(normalizedPersons)
        .map((id) => normalizedPersons[id])
        .map((p) => {
          return {
            id: p.id,
            name: { givenName: p.givenName, familyName: p.familyName },
            email: p.email,
            externalImageUrl: p.externalImageUrl,
            isSuggested: p.isSuggested,
          };
        }),
    [normalizedPersons],
  );

  // Remove selected values from the suggestion and enforce a max number of suggestions
  return suggestions.filter(({ email }) => !selections.includes(email)).slice(0, limit);
};

export const InvitePanel = createRefetchContainer(
  wrapWithClassComponent(InvitePanelCmpt), // wrapped with class component to satisfy createRefetchContainer's need for a ref,
  {
    org: fragments.org,
    billingGroupList: fragments.billingGroups,
  },
  query,
);

// TODO: In the future we should use TS types generated by relay, once configured
type QueryOperation = {
  response: {
    org: IOrg;
  };
  variables: {
    orgRelayId: string;
    queryInput: string;
  };
};

interface IInvitePanelRendererProps {
  orgRelayId: string;
  open: boolean;
  onClose: () => void;
  onCloseAfterInvite: () => void;
}

export const InvitePanelRenderer = ({
  orgRelayId,
  open,
  onClose,
  onCloseAfterInvite,
}: IInvitePanelRendererProps) => {
  const environment = getCurrentEnvironment();
  return (
    <QueryRenderer<QueryOperation>
      query={query}
      variables={{ orgRelayId, queryInput: "" }}
      environment={environment}
      render={({ props, error }) => {
        if (error || !props) {
          return null;
        }
        const billingGroups = getValue(props.org.billingGroups);
        return (
          <InvitePanel
            billingGroupList={billingGroups}
            open={open}
            onClose={onClose}
            onCloseAfterInvite={onCloseAfterInvite}
            org={props.org}
          />
        );
      }}
    />
  );
};
